import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'semantic-ui-react'
import styled from 'styled-components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { SubscriptionService } from 'utility/services/subscription'
import {
    loadStripe,
    resolveStripePublishableKey,
} from 'utility/stripe-loader'

const Body = styled.div`
    .title {
        font-size: 17px;
        font-weight: 700;
        margin: 0 0 6px;
    }
    .sub {
        font-size: 13px;
        color: #6b7280;
        margin: 0 0 14px;
    }
    .prep {
        text-align: center;
        padding: 28px 12px;
    }
    .prep .spin {
        width: 32px;
        height: 32px;
        margin: 16px auto 0;
        border: 3px solid #eee;
        border-top-color: #635bff;
        border-radius: 50%;
        animation: sp 0.8s linear infinite;
    }
    @keyframes sp {
        to {
            transform: rotate(360deg);
        }
    }
    #service-plan-pay-container {
        min-height: 180px;
        margin: 8px 0 12px;
    }
    .err {
        margin: 10px 0;
        padding: 10px 12px;
        border-radius: 8px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #991b1b;
        font-size: 13px;
    }
    .ok {
        text-align: center;
        padding: 24px 8px;
    }
    .ok .check {
        width: 56px;
        height: 56px;
        margin: 0 auto 12px;
        border-radius: 50%;
        background: #10b981;
        color: #fff;
        font-size: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
    }
    .actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 12px;
        flex-wrap: wrap;
    }
`

/**
 * Mockup Steps 3–5: PurchaseOrder → Stripe Elements → PaymentConfirm.
 * Isolated modal — does not alter other app screens.
 */
export const StripeCheckoutModal = ({
    open,
    plan,
    onClose,
    onSuccess,
}) => {
    const [phase, setPhase] = useState('prep') // prep | pay | success | error
    const [error, setError] = useState('')
    const [order, setOrder] = useState(null)
    const [paying, setPaying] = useState(false)
    const stripeRef = useRef(null)
    const elementsRef = useRef(null)

    const planId = plan?.PlanId ?? plan?.planId
    const planName = plan?.PlanName || plan?.planName || 'Premium'
    const amount = Number(
        order?.ChargeAmount ??
            order?.chargeAmount ??
            order?.PaymentAmount ??
            order?.paymentAmount ??
            plan?.PlanCost ??
            plan?.planCost ??
            0,
    )

    useEffect(() => {
        if (!open || !planId) return undefined
        let cancelled = false

        const start = async () => {
            setPhase('prep')
            setError('')
            setOrder(null)
            try {
                const created = await SubscriptionService.createPurchaseOrder({
                    planId,
                    isAutoRenewal: true,
                })
                if (cancelled) return
                setOrder(created)

                const clientSecret =
                    created?.ClientSecretId || created?.clientSecretId
                // Mockup Step 4: Stripe('pk_…') from /ServicePlan StripeClientKey,
                // then env, then PurchaseOrder fields.
                const publishableKey = resolveStripePublishableKey(
                    plan?.StripeClientKey ||
                        plan?.stripeClientKey ||
                        created,
                )
                if (!clientSecret) {
                    throw new Error(
                        'Purchase order did not return ClientSecretId',
                    )
                }
                if (!publishableKey) {
                    throw new Error(
                        'Stripe publishable key missing. Mockup expects StripeClientKey from GET /ServicePlan (or set REACT_APP_STRIPE_PUBLISHABLE_KEY).',
                    )
                }

                const stripe = await loadStripe(publishableKey)
                if (cancelled) return
                stripeRef.current = stripe
                const elements = stripe.elements({ clientSecret })
                elementsRef.current = elements
                setPhase('pay')

                // Mount after paint so #service-plan-pay-container exists
                requestAnimationFrame(() => {
                    const el = document.getElementById(
                        'service-plan-pay-container',
                    )
                    if (!el || cancelled) return
                    el.innerHTML = ''
                    const paymentElement = elements.create('payment')
                    paymentElement.mount('#service-plan-pay-container')
                })
            } catch (err) {
                if (cancelled) return
                setError(
                    err?.error?.message ||
                        err?.message ||
                        'Could not start payment. Please try again.',
                )
                setPhase('error')
            }
        }

        start()
        return () => {
            cancelled = true
        }
    }, [open, planId])

    const handlePay = async () => {
        if (!stripeRef.current || !elementsRef.current || !order) return
        setPaying(true)
        setError('')
        try {
            const { paymentIntent, error: stripeError } =
                await stripeRef.current.confirmPayment({
                    elements: elementsRef.current,
                    redirect: 'if_required',
                })
            if (stripeError) {
                setError(
                    stripeError.message ||
                        'Your card was declined. Please try a different card.',
                )
                // Do NOT call PaymentConfirm — charge never succeeded
                return
            }
            const status = paymentIntent?.status
            if (status !== 'succeeded' && status !== 'processing') {
                setError(`Payment status: ${status || 'unknown'}`)
                return
            }

            const confirmPayload = {
                PaymentOrderId:
                    order.PaymentOrderId || order.paymentOrderId || '',
                ChargeId:
                    paymentIntent?.latest_charge ||
                    paymentIntent?.charges?.data?.[0]?.id ||
                    order.ChargeId ||
                    '',
                TransactionId: order.TransactionId || order.transactionId || '',
                CreatedOn: order.CreatedOn || new Date().toISOString(),
                ResponseMessage: order.ResponseMessage || '',
                DisplayMessage: order.DisplayMessage || '',
                ChargeAmount:
                    order.ChargeAmount ?? order.chargeAmount ?? amount,
                status: 'SUCCEEDED',
                OrderNo: order.OrderNo ?? order.orderNo ?? 0,
                ClientSecretId:
                    order.ClientSecretId || order.clientSecretId || '',
                MerchantDisplayName: order.MerchantDisplayName || '',
                PaymentMethodId:
                    paymentIntent?.payment_method ||
                    order.PaymentMethodId ||
                    '',
                ThirdPartyClientId: order.ThirdPartyClientId || '',
                EphemeralKey: order.EphemeralKey || '',
                EphemeralKeySecret: order.EphemeralKeySecret || '',
                CompanyId: order.CompanyId ?? order.companyId ?? 0,
            }
            await SubscriptionService.confirmPayment(confirmPayload)
            setPhase('success')
            onSuccess?.(paymentIntent)
        } catch (err) {
            setError(
                err?.error?.message ||
                    err?.message ||
                    'Payment confirmation failed. Contact support if charged.',
            )
        } finally {
            setPaying(false)
        }
    }

    return (
        <Modal open={open} size="tiny" onClose={paying ? undefined : onClose}>
            <Modal.Content>
                <Body>
                    {phase === 'prep' && (
                        <div className="prep">
                            <div style={{ fontSize: 36 }}>🔒</div>
                            <div className="title">
                                Preparing secure payment…
                            </div>
                            <p className="sub">This takes about a second.</p>
                            <div className="spin" />
                        </div>
                    )}
                    {phase === 'pay' && (
                        <>
                            <div className="title">
                                Pay ${Number(amount).toFixed(2)}
                            </div>
                            <p className="sub">
                                {planName} · Powered by Stripe. Card details
                                never touch our server.
                            </p>
                            <div id="service-plan-pay-container" />
                            {error && <div className="err">{error}</div>}
                            <div className="actions">
                                <AppSecondaryButton
                                    type="button"
                                    onClick={onClose}
                                    disabled={paying}
                                >
                                    Cancel
                                </AppSecondaryButton>
                                <AppPrimaryButton
                                    type="button"
                                    loading={paying}
                                    onClick={handlePay}
                                >
                                    Pay ${Number(amount).toFixed(2)}
                                </AppPrimaryButton>
                            </div>
                        </>
                    )}
                    {phase === 'success' && (
                        <div className="ok">
                            <div className="check">✓</div>
                            <div className="title">
                                You&apos;re on {planName}!
                            </div>
                            <p className="sub">
                                Your plan is active. A receipt will be emailed
                                if configured on the server.
                            </p>
                            <AppPrimaryButton type="button" onClick={onClose}>
                                Back to Account
                            </AppPrimaryButton>
                        </div>
                    )}
                    {phase === 'error' && (
                        <>
                            <div className="title">Could not start payment</div>
                            {error && <div className="err">{error}</div>}
                            <div className="actions">
                                <AppSecondaryButton
                                    type="button"
                                    onClick={onClose}
                                >
                                    Close
                                </AppSecondaryButton>
                            </div>
                        </>
                    )}
                </Body>
            </Modal.Content>
        </Modal>
    )
}
